// Planning → Rooster: wie doet wat wanneer — taken, draaidagen, grote afspraken en
// afwezigheid, per persoon per dag. Bestaande blokken verslepen/uitklappen blijft (snelle
// correctie); het inplannen van nieuw werk (de "te plannen"-bak) staat sinds 25 sep op
// Nog in te plannen, dat hetzelfde RoosterGrid hergebruikt (Joris: "splitsen en hergebruiken").
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Button, Chip, CircularProgress, Link } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { getMonday, getWeekNumber } from '../../utils/weekUtils';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import AfgerondSignaal from '../../ui-components/AfgerondSignaal';
import WeekSelector from '../../ui-components/WeekSelector';
import { eersteTaakDatum } from './rooster';
import { addDays, formatISO, lokaleDatum } from './plannerDatums';
import useRooster, { groepeerPersonen, STAGIAIRS_SLEUTEL } from './useRooster';
import RoosterGrid, { RoosterWeergave, RoosterZoek } from './RoosterGrid';

const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';

const leesOpslag = (k, standaard) => { try { return localStorage.getItem(k) ?? standaard; } catch { return standaard; } };
const zetOpslag = (k, v) => { try { localStorage.setItem(k, v); } catch { /* privémodus */ } };

export default function PlannerTodo() {
  const [searchParams] = useSearchParams();
  // /capacity linkt hierheen met ?persoon=<record-id>&datum=YYYY-MM-DD ("Werk inplannen").
  const linkPersoon = searchParams.get('persoon');
  const linkDatum = lokaleDatum(searchParams.get('datum'));

  const [weekStart, setWeekStart] = useState(() => getMonday(linkDatum || new Date()));
  const [numWeken, setNumWeken] = useState(() => {
    const n = Number(leesOpslag('plannerWeken', 2));
    return [1, 2, 4].includes(n) ? n : 2;
  });
  const kiesWeken = (n) => { setNumWeken(n); zetOpslag('plannerWeken', String(n)); };
  const [sortering, setSortering] = useState(() => (leesOpslag('plannerSortering', 'ruimte') === 'naam' ? 'naam' : 'ruimte'));
  const kiesSortering = (s) => { setSortering(s); zetOpslag('plannerSortering', s); };
  const [toonFreelancers, setToonFreelancers] = useState(false);
  // Stagiairs tonen: standaard aan, keuze onthouden (zoals venster en sortering).
  const [toonStagiairs, setToonStagiairs] = useState(() => leesOpslag(STAGIAIRS_SLEUTEL, '1') !== '0');
  const kiesStagiairs = (aan) => { setToonStagiairs(aan); zetOpslag(STAGIAIRS_SLEUTEL, aan ? '1' : '0'); };
  const [functionFilter, setFunctionFilter] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(linkPersoon || null);

  const rooster = useRooster({ weekStart, numWeken });
  const { loading, verversend, persons, labels, belasting, blokkenPerPersoon, existingTasks, kolommen } = rooster;

  const zoek = searchTerm.trim().toLowerCase();
  const { groepen } = useMemo(() => groepeerPersonen({
    persons, labels, belasting, zoek, toonFreelancers, toonStagiairs, functionFilter, sortering, selectedPerson,
  }), [persons, labels, belasting, zoek, toonFreelancers, toonStagiairs, functionFilter, sortering, selectedPerson]);

  // ── Overzicht: hoe staat het ervoor in dit venster? ────────────────────────
  const zichtbareIds = useMemo(() => Object.values(groepen).flat().map(p => p.id), [groepen]);
  const samenvatting = useMemo(() => {
    const ids = new Set();
    let vol = 0;
    zichtbareIds.forEach((id) => {
      (blokkenPerPersoon[id]?.geplaatst || []).forEach(b => { if (!b.vast) b.tasks.forEach(t => ids.add(t.id)); });
      if (belasting[id]?.totaal.overboekt) vol += 1;
    });
    return { ingepland: ids.size, vol };
  }, [zichtbareIds, blokkenPerPersoon, belasting]);
  const leegVenster = !loading && zichtbareIds.length > 0 && samenvatting.ingepland === 0;

  // Leeg venster? Zoek de eerstvolgende week met taken: eerst in wat er al binnen is
  // (5 weken), anders één extra lookahead tot 16 weken vooruit.
  const [volgende, setVolgende] = useState({ sleutel: null, datum: null, bezig: false });
  const eindVenster = kolommen[kolommen.length - 1]?.iso;
  const idsSleutel = [...zichtbareIds].sort().join(','); // volgorde wisselt met de sortering — niet opnieuw zoeken
  const volgendeRef = useRef(null);
  useEffect(() => {
    if (!leegVenster || !eindVenster) return;
    const ids = idsSleutel.split(',').filter(Boolean);
    const sleutel = `${formatISO(weekStart)}:${eindVenster}:${idsSleutel}`;
    if (volgendeRef.current === sleutel) return;
    volgendeRef.current = sleutel;
    const naVenster = formatISO(addDays(lokaleDatum(eindVenster), 1));
    const alBinnen = eersteTaakDatum(existingTasks, ids, naVenster);
    if (alBinnen) { setVolgende({ sleutel, datum: alBinnen, bezig: false }); return; }
    setVolgende({ sleutel, datum: null, bezig: true });
    const params = new URLSearchParams();
    params.append('startDate', formatISO(addDays(weekStart, 35)));
    params.append('endDate', formatISO(addDays(weekStart, 16 * 7)));
    ids.forEach(id => params.append('personIds[]', id));
    const klaar = (datum) => { if (volgendeRef.current === sleutel) setVolgende({ sleutel, datum, bezig: false }); };
    authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`)
      .then(r => leesJson(r, 'Vooruitkijken naar taken'))
      .then(d => klaar(eersteTaakDatum(d, ids, naVenster)))
      .catch(() => klaar(null));
  }, [leegVenster, eindVenster, weekStart, idsSleutel, existingTasks]);
  const volgendeWeek = leegVenster && volgende.datum && volgende.sleutel === volgendeRef.current ? getMonday(lokaleDatum(volgende.datum)) : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedPersonName = selectedPerson ? (labels[selectedPerson] || '') : '';
  const inplanLink = selectedPerson
    ? `/planning/inplannen?${new URLSearchParams({ persoon: selectedPerson, datum: formatISO(linkPersoon === selectedPerson && linkDatum ? linkDatum : weekStart) })}`
    : null;

  return (
    <Box sx={{ width: '100%', mx: 'auto', px: { xs: 1.5, md: 3 }, pt: { xs: 1.5, md: 2 }, pb: 1.5, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Taken die al op Afgerond staan en daardoor onzichtbaar zijn (9 okt) — alleen als er zijn */}
      <AfgerondSignaal sx={{ flexShrink: 0 }} />
      {/* Eén toolbarregel; de schermnaam staat als tab "Rooster" in de kopbalk (PlanningTabs) */}
      <PageHeader
        kaal
        title={(
          <Box component="span" sx={{ fontFamily: '"Inter", system-ui, sans-serif', fontSize: 12, fontWeight: 400, letterSpacing: 0, color: GEDEMPT, whiteSpace: 'nowrap' }}>
            {samenvatting.ingepland} {samenvatting.ingepland === 1 ? 'taak' : 'taken'} ingepland · {samenvatting.vol} {samenvatting.vol === 1 ? 'persoon' : 'personen'} vol
          </Box>
        )}
        subtitle={leegVenster ? (
          <Box component="span" sx={{ fontSize: 12, color: GEDEMPT }}>
            Geen ingeplande taken {numWeken === 1 ? 'deze week' : `deze ${numWeken} weken`}
            {volgendeWeek ? (
              <> · <Link component="button" underline="hover" onClick={() => setWeekStart(volgendeWeek)}
                sx={{ fontSize: 12, color: TEKST, fontWeight: 600, verticalAlign: 'baseline' }}>
                naar wk {getWeekNumber(volgendeWeek)} ›
              </Link></>
            ) : volgende.bezig ? ' · vooruitkijken…' : ' · ook de komende 16 weken niet'}
          </Box>
        ) : undefined}
        rightActions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {verversend && (
              <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: GEDEMPT }}>
                <CircularProgress size={10} thickness={6} sx={{ color: GEDEMPT }} />
                <Typography component="span" sx={{ fontSize: 11, color: GEDEMPT }}>bijwerken…</Typography>
              </Box>
            )}
            <RoosterWeergave
              rooster={rooster}
              functionFilter={functionFilter} onFunctionFilter={setFunctionFilter}
              toonFreelancers={toonFreelancers} onToonFreelancers={setToonFreelancers}
              toonStagiairs={toonStagiairs} onToonStagiairs={kiesStagiairs}
              numWeken={numWeken} onWeken={kiesWeken}
            />
            <WeekSelector weekStart={weekStart} onChange={setWeekStart} minWidth={150} />
            <Button size="small" variant="outlined" onClick={() => setWeekStart(getMonday(new Date()))}
              sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 1.25, py: 0.3, borderColor: '#cbd5e1', color: '#334155' }}>
              Vandaag
            </Button>
          </Box>
        )}
      />

      <RoosterGrid
        rooster={rooster}
        groepen={groepen}
        selectedPerson={selectedPerson}
        onSelectPerson={setSelectedPerson}
        kopLinks={<RoosterZoek zoekterm={searchTerm} onZoekterm={setSearchTerm} sortering={sortering} onSortering={kiesSortering} placeholder="Zoek persoon" />}
      />

      {/* Geselecteerde persoon: werk voor hem/haar inplannen gebeurt op Nog in te plannen */}
      {selectedPerson && (
        <Box sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
          <Chip
            icon={<PersonIcon />}
            label={<>{selectedPersonName} geselecteerd · <Box component={RouterLink} to={inplanLink} sx={{ color: 'inherit', fontWeight: 700 }}>werk inplannen ›</Box></>}
            color="primary"
            onDelete={() => setSelectedPerson(null)}
            sx={{ py: 2, px: 1, fontSize: 12, fontWeight: 500, boxShadow: '0 4px 14px rgba(15,23,42,.25)' }}
          />
        </Box>
      )}
    </Box>
  );
}
